import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Users, Maximize2, Minimize2 } from 'lucide-react';
import { api } from '../services/api';

// Compact 12hr time: "2pm", "2:30pm".
const formatShortTime = (date) => {
  const h = date.getHours();
  const m = date.getMinutes();
  const meridiem = h >= 12 ? 'pm' : 'am';
  const h12 = h % 12 || 12;
  return m === 0 ? `${h12}${meridiem}` : `${h12}:${String(m).padStart(2, '0')}${meridiem}`;
};

/**
 * Fullscreen QR presentation for event hosts.
 *
 * Design intent:
 * - The QR fills the viewport — optimized for being cast on a 75"/100" TV.
 * - The QR rotates every ~20s (well under the scan-token TTL) so the on-screen
 *   code is always fresh and a phone-photo shared later goes stale quickly.
 *   The scan token only proves "this QR was displayed recently"; the actual
 *   check-in authenticates via a longer-lived session token minted when the
 *   attendee first hits /attend/<scan_token>. See qr_token_service.
 * - A live attendee counter (confirmed + pending) gives the host a pulse of the room.
 * - Press F / click the fullscreen button to hit real fullscreen for the display.
 * - ESC / Back returns to the event detail page.
 */
const EventPresent = () => {
  const { eventId } = useParams();
  const [token, setToken] = useState(null);
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const rootRef = useRef(null);

  // Event meta
  const { data: event } = useQuery({
    queryKey: ['attendance-events', eventId, 'present-meta'],
    queryFn: () => api.get(`/attendance-events/events/${eventId}/`).then(r => r.data),
    enabled: !!eventId,
  });

  // Live attendee counts
  const { data: attendees } = useQuery({
    queryKey: ['attendance-events', eventId, 'present-attendees'],
    queryFn: () => api.get(`/attendance-events/events/${eventId}/attendees/`).then(r => r.data),
    enabled: !!eventId,
    refetchInterval: 4000,
  });

  // Rotate QR token
  useEffect(() => {
    if (!eventId) return;
    let active = true;
    const rotate = async () => {
      try {
        const { data } = await api.get(`/attendance-events/events/${eventId}/qr-token/`);
        if (!active) return;
        setToken(data.token);
        setQrDataUrl(data.qr_png_data_url);
        setLoading(false);
        setErr(null);
      } catch (e) {
        if (!active) return;
        setLoading(false);
        setErr(e?.response?.data?.error || 'Event is not active. Activate it first to show a QR.');
      }
    };
    rotate();
    const id = setInterval(rotate, 20_000);
    return () => { active = false; clearInterval(id); };
  }, [eventId]);

  // Fullscreen
  useEffect(() => {
    const onFs = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      rootRef.current?.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  };

  // Keyboard: F for fullscreen
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'f' || e.key === 'F') toggleFullscreen();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const confirmedCount = (attendees || []).filter(a => a.status === 'confirmed').length;
  const pendingCount = (attendees || []).filter(a => a.status === 'pending_approval').length;

  return (
    <div
      ref={rootRef}
      className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-900 to-blue-950 text-white flex flex-col"
    >
      {/* Top bar — icon-only circular nav buttons per DS (`.icon-button-dark`).
          `title` provides a hover tooltip; `aria-label` carries the accessible name. */}
      <div className="flex items-center justify-between px-3 sm:px-4 md:px-8 py-3 sm:py-4 border-b border-white/10">
        <Link
          to={`/self-service/events/${eventId}`}
          title="Back to event"
          aria-label="Back to event"
          className="icon-button-dark"
        >
          <ArrowLeft className="w-4 h-4" />
        </Link>
        <button
          type="button"
          onClick={toggleFullscreen}
          title={isFullscreen ? 'Exit fullscreen (F)' : 'Enter fullscreen (F)'}
          aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
          className="icon-button-dark"
        >
          {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
        </button>
      </div>

      {/* Body — fluid layout. Stacks on mobile/tablet, side-by-side on lg+.
          DOM order puts the QR first so it appears above the meta when stacked
          on mobile (QR is the primary action; meta is supporting context).
          QR size uses `vmin` so it scales with the smaller dimension of the
          viewport (works for both portrait phones and 100" landscape TVs). */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-5 gap-6 md:gap-10 px-4 py-5 sm:px-6 md:p-10 items-center">
        {/* QR — on top when stacked, left column on lg+. Fluid square that scales with viewport. */}
        <div className="lg:col-span-3 lg:order-1 flex flex-col items-center justify-center">
          <div className="bg-white rounded-3xl shadow-2xl p-4 sm:p-6 md:p-8 xl:p-10 relative">
            <div
              className="flex items-center justify-center"
              style={{
                // Square sized to the smaller viewport dimension, capped so it
                // never overflows on mobile and never looks tiny on a 4K TV.
                width: 'min(70vmin, 640px)',
                height: 'min(70vmin, 640px)',
                minWidth: '240px',
                minHeight: '240px',
              }}
            >
              {loading && (
                <div className="text-gray-500 text-sm">Loading…</div>
              )}
              {err && !loading && (
                <div className="text-red-600 text-center text-sm px-6">
                  {err}
                </div>
              )}
              {!loading && !err && qrDataUrl && (
                <img
                  src={qrDataUrl}
                  alt="Scan to check in"
                  className="w-full h-full block"
                  draggable={false}
                />
              )}
            </div>
            <div className="absolute -top-2 sm:-top-3 right-3 sm:right-4 px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full bg-blue-800 text-white text-[10px] sm:text-xs font-semibold shadow">
              LIVE
            </div>
          </div>
          <p className="mt-4 md:mt-6 text-white/70 text-sm sm:text-base md:text-xl font-medium text-center max-w-md px-4">
            Scan with your phone camera to check in
          </p>
          <p className="mt-1 md:mt-2 text-white/40 text-[11px] sm:text-xs text-center px-4">
            Code rotates automatically for security.
          </p>
        </div>

        {/* Event meta — below the QR when stacked, right column on lg+. */}
        <div className="lg:col-span-2 lg:order-2 space-y-3 sm:space-y-4 md:space-y-6 text-center lg:text-left">
          {event && (
            <>
              <div className="flex items-center gap-2 sm:gap-3 flex-wrap justify-center lg:justify-start">
                <div className="inline-block px-3 py-1 rounded-full text-[11px] sm:text-xs font-semibold bg-white/10 text-white/80 uppercase tracking-widest">
                  {event.event_type}
                </div>
                {event.reference_no && (
                  <div className="inline-block px-3 py-1 rounded-full text-[11px] sm:text-xs font-mono font-semibold bg-white/5 text-white/60 tracking-widest border border-white/10">
                    {event.reference_no}
                  </div>
                )}
              </div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl xl:text-6xl font-bold leading-tight">
                {event.title}
              </h1>
              <div className="space-y-0.5 sm:space-y-1 text-white/70 text-sm sm:text-base md:text-lg">
                <p>
                  {new Date(event.start_at).toLocaleString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                  <span className="text-white/40"> · </span>
                  {formatShortTime(new Date(event.start_at))}
                </p>
                {event.location && <p>{event.location}</p>}
                {event.project?.project_code && (
                  <p className="text-blue-200">
                    <span className="font-mono font-semibold">{event.project.project_code}</span>
                    <span className="text-white/60"> · {event.project.project_name}</span>
                  </p>
                )}
              </div>

              <div className="inline-flex items-center gap-3 px-4 sm:px-5 py-2 sm:py-3 rounded-2xl bg-white/5 border border-white/10">
                <Users className="w-5 h-5 sm:w-6 sm:h-6 text-blue-300" />
                <div className="text-left">
                  <div className="text-2xl sm:text-3xl font-bold tabular-nums leading-none">{confirmedCount}</div>
                  <div className="text-[10px] sm:text-xs text-white/60 uppercase tracking-wider mt-1">
                    Checked in{pendingCount ? ` · ${pendingCount} pending` : ''}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default EventPresent;
